import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useLoading } from "../../LoadingContext"
import { useConnectionError } from "../../ConnectionErrorContext"
import DashboardLayout from "../../components/DashboardLayout"
import DashboardNavbar from "../../components/DashboardNavbar"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import MDButton from "components/MDButton"
import { Checkbox, FormControlLabel } from "@mui/material"
import { SENSITIVITY_OPTIONS, toSensitivityOption } from "../../../constants/sensitivity"
import MDInput from "components/MDInput"
import { Card } from "@mui/material"
import { getCatvData } from "../../../api/get_catv"
import { manageCatvUpdate } from "../../../api/manage_catv_update"
import { ManageCatvData } from "../catv"

export interface MonthlyReportData {
  filename: string
  year_month: string
  url: string
}
export interface DailyReportData {
  filename: string
  date: string
  url: string
}
const DetectSettingPage: React.FC = () => {
  const navigate = useNavigate()

  // 通信関連
  const { startLoading, stopLoading } = useLoading()
  const [catvData, setCatvData] = useState<ManageCatvData>()
  const { showConnectionError } = useConnectionError()
  const [formData, setFormData] = useState({
    id: "",
    name: "",
    address: "",
    phone: "",
    sensitivityEnabled: false,
    sensitivity: "96",
    detectionEnabled: false,
    detectionPeriodStart: "00:00", // 最初の期間をセット
    detectionPeriodEnd: "00:00", // 最後の期間をセット
    optionEnabled: false,
    optionPeriodStart: "00:00", // 最初のオプション期間
    optionPeriodEnd: "00:00", // 最後のオプション期間
    criticalCountEnabled: false,
    criticalCount: "90"
  })

  const [errors, setErrors] = useState({
    id: "",
    name: "",
    address: "",
    phone: "",
    sensitivity: "",
    detectionPeriodStart: "",
    detectionPeriodEnd: "",
    optionPeriodStart: "",
    optionPeriodEnd: "",
    criticalCount: "",
    server: ""
  })

  // 開発者アカウントでの閲覧を可能とする
  const [isViewMode, setIsViewMode] = useState<boolean>(false)

  // 未ログインの場合はエラー
  if (localStorage.getItem("session_code") == "") {
    navigate("/login")
    return <></>
  }

  // 開発者アカウント以外でログインした場合はエラー
  const auth = localStorage.getItem("account_type")
  if (auth == "estate") {
    navigate("/") // 不正アクセス
  }

  // 初期表示時の処理
  const fetchManageCatvData = async () => {
    const auth = localStorage.getItem("account_type")
    if (auth == "service") {
      let isViewModeWork = true
      const view_mode = localStorage.getItem("view_mode")
      if (view_mode == "2") {
        const estate_id = localStorage.getItem("show_mode_estate")
        if (estate_id === "" || estate_id === "-") {
          isViewModeWork = false
        }
      }
      setIsViewMode(isViewModeWork)
    }

    try {
      startLoading()
      // サービス提供元のデータを取得する
      const response = await getCatvData()
      const myCatvData = response.catv_data
      setCatvData(myCatvData)

      // formデータに設定する
      setFormData({
        id: myCatvData.id,
        name: myCatvData.name,
        address: myCatvData.address,
        phone: myCatvData.phone,
        sensitivityEnabled: myCatvData.sensitivity_enabled === 1,
        sensitivity: toSensitivityOption(myCatvData.sensitivity).toString(),
        detectionEnabled: myCatvData.detection_enabled === 1,
        detectionPeriodStart: myCatvData.detection_period[0] || "00:00", // 最初の期間をセット
        detectionPeriodEnd: myCatvData.detection_period[1] || "00:00", // 最後の期間をセット
        optionEnabled: myCatvData.option_enabled === 1,
        optionPeriodStart: myCatvData.option_period[0] || "00:00", // 最初のオプション期間
        optionPeriodEnd: myCatvData.option_period[1] || "00:00", // 最後のオプション期間
        criticalCountEnabled: myCatvData.critical_count_enabled === 1,
        criticalCount: myCatvData.critical_count.toString()
      })
    } catch (error: any) {
      console.log(error)
      //navigate("/")
    } finally {
      stopLoading()
    }
  }
  useEffect(() => {
    fetchManageCatvData()
  }, [])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const target = e.target as HTMLInputElement // 明示的に型キャスト
    const { name, value, type, checked } = target
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }))
  }

  const validate = () => {
    const validationErrors = { ...errors }
    let hasError = false

    // 絵文字を除外するための正規表現
    const emojiRegex = /(?:\p{Extended_Pictographic}(?![\u3190-\u319F\u3300-\u33FF]))+/gu

    // 必須項目のバリデーション
    // チェックボックスがオンの場合のバリデーション
    if (formData.sensitivityEnabled && formData.sensitivity === "") {
      validationErrors.sensitivity = "必須項目が入力されていません"
      hasError = true
    } else {
      validationErrors.sensitivity = ""
    }

    if (formData.detectionEnabled && (formData.detectionPeriodStart === "" || formData.detectionPeriodEnd === "")) {
      validationErrors.detectionPeriodStart = "必須項目が入力されていません"
      validationErrors.detectionPeriodEnd = "必須項目が入力されていません"
      hasError = true
    } else {
      validationErrors.detectionPeriodStart = ""
      validationErrors.detectionPeriodEnd = ""
    }

    if (formData.optionEnabled && (formData.optionPeriodStart === "" || formData.optionPeriodEnd === "")) {
      validationErrors.optionPeriodStart = "必須項目が入力されていません"
      validationErrors.optionPeriodEnd = "必須項目が入力されていません"
      hasError = true
    } else {
      validationErrors.optionPeriodStart = ""
      validationErrors.optionPeriodEnd = ""
    }

    if (formData.criticalCountEnabled && formData.criticalCount === "") {
      validationErrors.criticalCount = "必須項目が入力されていません"
      hasError = true
    } else {
      validationErrors.criticalCount = ""
    }

    validationErrors.server = ""

    setErrors(validationErrors)
    return !hasError
  }

  const convertEnabledValues = (data: any) => {
    return {
      ...data,
      sensitivityEnabled: data.sensitivityEnabled ? 1 : 0,
      detectionEnabled: data.detectionEnabled ? 1 : 0,
      optionEnabled: data.optionEnabled ? 1 : 0,
      criticalCountEnabled: data.criticalCountEnabled ? 1 : 0
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (validate()) {
      // バリデーション成功時の処理
      try {
        startLoading()
        const convertedData = convertEnabledValues(formData)
        await manageCatvUpdate(
          convertedData.id,
          convertedData.name,
          convertedData.address,
          convertedData.phone,
          convertedData.sensitivityEnabled,
          convertedData.sensitivity,
          convertedData.detectionEnabled,
          convertedData.detectionPeriodStart,
          convertedData.detectionPeriodEnd,
          convertedData.optionEnabled,
          convertedData.optionPeriodStart,
          convertedData.optionPeriodEnd,
          convertedData.criticalCountEnabled,
          convertedData.criticalCount
        )
        //console.log(result);
      } catch (error: any) {
        if (error.response && error.response.status === 401) {
          navigate("/login") // セッション切れ時にトップページへリダイレクト
        } else if (error.response) {
          const errorDetail = error.response.data.detail
          const validationErrors = {
            id: "",
            name: "",
            address: "",
            phone: "",
            sensitivity: "",
            detectionPeriodStart: "",
            detectionPeriodEnd: "",
            optionPeriodStart: "",
            optionPeriodEnd: "",
            criticalCount: "",
            server: ""
          }
          validationErrors.server = errorDetail.message
          setErrors(validationErrors)
        } else {
          const validationErrors = {
            id: "",
            name: "",
            address: "",
            phone: "",
            sensitivity: "",
            detectionPeriodStart: "",
            detectionPeriodEnd: "",
            optionPeriodStart: "",
            optionPeriodEnd: "",
            criticalCount: "",
            server: ""
          }
          validationErrors.server = "サーバーへの接続に失敗しました"
          setErrors(validationErrors)
        }
      } finally {
        stopLoading()
      }
    }
  }

  return (
    <DashboardLayout>
      <MDBox mb={4}>
        <DashboardNavbar absolute={true} />
      </MDBox>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" pt={3} pb={3}>
        <MDBox>
          <MDTypography variant="h4" gutterBottom>
            検知設定
          </MDTypography>
        </MDBox>
      </MDBox>

      {/* 検知設定 */}
      <Card
        sx={{
          height: "100%",
          paddingTop: "20px",
          paddingBottom: "20px",
          justifyContent: "center",
          alignItems: "center",
          marginBottom: "20px"
        }}
      >
        <MDBox sx={{ width: "360px" }}>
          <MDBox display="flex" alignItems="center" gap={1}>
            <FormControlLabel
              control={
                <Checkbox
                  name="sensitivityEnabled"
                  checked={formData.sensitivityEnabled}
                  onChange={handleChange}
                  readOnly={isViewMode}
                  disabled={isViewMode}
                />
              }
              label="感度設定を有効化"
            />
          </MDBox>
          <MDBox display="flex" alignItems="center" gap={1}>
            <MDInput
              select
              SelectProps={{ native: true }}
              label="感度"
              name="sensitivity"
              value={formData.sensitivity}
              onChange={handleChange}
              disabled={!formData.sensitivityEnabled || isViewMode}
              sx={{ width: "356px" }}
            >
              {SENSITIVITY_OPTIONS.map((option) => (
                <option key={option.value} value={String(option.value)}>
                  {option.label}
                </option>
              ))}
            </MDInput>
          </MDBox>
          {errors.sensitivity && (
            <MDBox
              color="#FF0000" // テキスト色
              pl={2} // 左パディング
              mb={2} // 下マージン
              fontSize="0.7em" // フォントサイズ
            >
              {errors.sensitivity}
            </MDBox>
          )}
          <MDBox display="flex" alignItems="center" gap={1}>
            <FormControlLabel
              control={
                <Checkbox
                  name="detectionEnabled"
                  checked={formData.detectionEnabled}
                  onChange={handleChange}
                  readOnly={isViewMode}
                  disabled={isViewMode}
                />
              }
              label="不在検知時間帯を有効化"
            />
          </MDBox>
          <MDBox display="flex" alignItems="center" gap={1}>
            <MDInput
              type="time"
              name="detectionPeriodStart"
              value={formData.detectionPeriodStart}
              onChange={handleChange}
              sx={{ width: "160px" }}
              disabled={!formData.detectionEnabled || isViewMode}
            />
            〜
            <MDInput
              type="time"
              name="detectionPeriodEnd"
              value={formData.detectionPeriodEnd}
              onChange={handleChange}
              sx={{ width: "160px" }}
              disabled={!formData.detectionEnabled || isViewMode}
            />
          </MDBox>
          {errors.detectionPeriodStart && (
            <MDBox
              color="#FF0000" // テキスト色
              pl={2} // 左パディング
              mb={2} // 下マージン
              fontSize="0.7em" // フォントサイズ
            >
              {errors.detectionPeriodStart}
            </MDBox>
          )}
          {errors.detectionPeriodEnd && (
            <MDBox
              color="#FF0000" // テキスト色
              pl={2} // 左パディング
              mb={2} // 下マージン
              fontSize="0.7em" // フォントサイズ
            >
              {errors.detectionPeriodEnd}
            </MDBox>
          )}
          <MDBox display="flex" alignItems="center" gap={1}>
            <FormControlLabel
              control={
                <Checkbox
                  name="optionEnabled"
                  checked={formData.optionEnabled}
                  onChange={handleChange}
                  readOnly={isViewMode}
                  disabled={isViewMode}
                />
              }
              label="不在検知時間帯(オプション)を有効化"
            />
          </MDBox>
          <MDBox display="flex" alignItems="center" gap={1}>
            <MDInput
              type="time"
              name="optionPeriodStart"
              value={formData.optionPeriodStart}
              onChange={handleChange}
              sx={{ width: "160px" }}
              disabled={!formData.optionEnabled || isViewMode}
            />
            〜
            <MDInput
              type="time"
              name="optionPeriodEnd"
              value={formData.optionPeriodEnd}
              onChange={handleChange}
              sx={{ width: "160px" }}
              disabled={!formData.optionEnabled || isViewMode}
            />
          </MDBox>
          {errors.optionPeriodStart && (
            <MDBox
              color="#FF0000" // テキスト色
              pl={2} // 左パディング
              mb={2} // 下マージン
              fontSize="0.7em" // フォントサイズ
            >
              {errors.optionPeriodStart}
            </MDBox>
          )}
          {errors.optionPeriodEnd && (
            <MDBox
              color="#FF0000" // テキスト色
              pl={2} // 左パディング
              mb={2} // 下マージン
              fontSize="0.7em" // フォントサイズ
            >
              {errors.optionPeriodEnd}
            </MDBox>
          )}
          {errors.server && (
            <MDBox
              color="#FF0000" // テキスト色
              pl={2} // 左パディング
              mb={2} // 下マージン
              fontSize="0.7em" // フォントサイズ
            >
              {errors.server}
            </MDBox>
          )}
          {!isViewMode && (
            <MDBox mt={4} mb={2}>
              <MDButton
                variant="contained"
                color="info"
                fullWidth
                sx={{
                  borderRadius: "4px",
                  textTransform: "none",
                  fontSize: "1rem",
                  padding: "0.75rem 0",
                  backgroundColor: "#1976d2",
                  "&:hover": {
                    backgroundColor: "#1565c0"
                  }
                }}
                onClick={(e) => {
                  handleSubmit(e)
                }}
              >
                更新
              </MDButton>
            </MDBox>
          )}
        </MDBox>
      </Card>
    </DashboardLayout>
  )
}

export default DetectSettingPage
